import api from "../api";

class SpacesService {
  /**
   * Get available spaces with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.perPage - Items per page
   * @returns {Promise<Object>} - Available spaces data
   */
  async getAvailableSpaces(params = {}) {
    try {
      const response = await api.get("/spaces/available", {
        params: {
          page: params.page || 1,
          per_page: params.perPage || 10,
          ...params,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching available spaces:", error);
      throw error;
    }
  }

  /**
   * Get available spaces info for a specific user
   * @param {number} userId - User ID
   * @returns {Promise<Object>} - Available spaces info
   */
  async getAvailableSpacesInfo(userId) {
    try {
      const response = await api.get(`/spaces/available-info/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching available spaces info:", error);
      throw error;
    }
  }

  /**
   * Keep me informed about a property (Laravel compatible)
   * @param {number} propertyId - Property ID
   * @returns {Promise<Object>} - Response data
   */
  async keepMeInformed(propertyId) {
    try {
      // Try Laravel compatible GET method first
      try {
        const response = await api.get(`/spaces/keep-informed/${propertyId}`);
        return response.data;
      } catch (getError) {
        console.log(
          "GET method failed, falling back to POST method:",
          getError.message
        );
        // Fallback to legacy POST method
        const response = await api.post(`/spaces/keep-me-inform/${propertyId}`);
        return response.data;
      }
    } catch (error) {
      console.error("Error keeping informed:", error);
      throw error;
    }
  }

  /**
   * Stop notifications for a property
   * @param {number} propertyId - Property ID
   * @returns {Promise<Object>} - Response data
   */
  async stopNotifications(propertyId) {
    try {
      const response = await api.delete(`/spaces/keep-me-inform/${propertyId}`);
      return response.data;
    } catch (error) {
      console.error("Error stopping notifications:", error);
      throw error;
    }
  }

  /**
   * Request viewing for a property
   * @param {number} propertyId - Property ID
   * @param {Object} viewingData - Viewing request data
   * @returns {Promise<Object>} - Response data
   */
  async requestViewing(propertyId, viewingData) {
    try {
      const response = await api.post(
        `/spaces/request-viewing/${propertyId}`,
        viewingData
      );
      return response.data;
    } catch (error) {
      console.error("Error requesting viewing:", error);
      throw error;
    }
  }

  /**
   * Get property viewing confirmations for current user
   * @returns {Promise<Object>} - Viewing confirmations data
   */
  async getMyConfirmations() {
    try {
      const response = await api.get("/spaces/my-confirmations");
      return response.data;
    } catch (error) {
      console.error("Error fetching confirmations:", error);
      throw error;
    }
  }

  /**
   * Get property viewing info for current user
   * @returns {Promise<Object>} - Viewing info data
   */
  async getPropertyViewingInfo() {
    try {
      const response = await api.get("/spaces/keep-me-inform");
      return response.data;
    } catch (error) {
      console.error("Error fetching property viewing info:", error);
      throw error;
    }
  }

  /**
   * Get property details by ID
   * @param {number} propertyId - Property ID
   * @returns {Promise<Object>} - Property details
   */
  async getPropertyDetails(propertyId) {
    try {
      const response = await api.get(`/spaces/show/${propertyId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching property details:", error);
      throw error;
    }
  }

  /**
   * Get property details by property ID (alternative route)
   * @param {string} propertyId - Property ID
   * @returns {Promise<Object>} - Property details
   */
  async getPropertyDetailsByPropertyId(propertyId) {
    try {
      const response = await api.get(
        `/spaces/available/propertyID/${propertyId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching property details by property ID:", error);
      throw error;
    }
  }

  /**
   * Get upcoming viewings for property (for request viewing modal)
   * LIVE DATABASE ONLY - throws error on failure
   * @param {number} propertyId - Property ID
   * @returns {Promise<Object>} - Upcoming viewings data
   */
  async getUpcomingViewingsForProperty(propertyId) {
    try {
      console.log(
        `🔍 [SERVICE] Getting upcoming viewings for property ${propertyId}`
      );

      const response = await api.post(
        `/propertyviewings/property/${propertyId}/request-viewing`
      );

      if (response.data?.success) {
        console.log(`✅ [SERVICE] Got upcoming viewings:`, response.data.data);
        return response.data.data;
      } else {
        throw new Error(
          response.data?.message || "Failed to get upcoming viewings"
        );
      }
    } catch (error) {
      console.error(
        "❌ [SERVICE] API failed for upcoming viewings:",
        error.message
      );

      // Re-throw error instead of returning empty data - LIVE DATABASE ONLY
      throw error;
    }
  }

  /**
   * Confirm attendance for a viewing (I will attend)
   * @param {number} viewingId - Viewing ID
   * @returns {Promise<Object>} - Confirmation data
   */
  async confirmViewingAttendance(viewingId) {
    try {
      console.log(
        `🔍 [SERVICE] Confirming attendance for viewing ${viewingId}`
      );

      const response = await api.post(
        `/propertyviewings/viewing/${viewingId}/attend`
      );

      if (response.data?.success) {
        console.log(`✅ [SERVICE] Confirmed attendance:`, response.data.data);
        return response.data.data;
      } else {
        throw new Error(
          response.data?.message || "Failed to confirm attendance"
        );
      }
    } catch (error) {
      console.error(
        "❌ [SERVICE] API failed for confirm attendance:",
        error.message
      );

      // Re-throw error instead of providing mock response
      throw error;
    }
  }

  /**
   * Cancel attendance for a viewing
   * @param {number} viewingId - Viewing ID
   * @returns {Promise<Object>} - Cancellation data
   */
  async cancelViewingAttendance(viewingId) {
    try {
      console.log(
        `🔍 [SERVICE] Cancelling attendance for viewing ${viewingId}`
      );

      const response = await api.delete(
        `/propertyviewings/viewing/${viewingId}/cancel`
      );

      if (response.data?.success) {
        console.log(`✅ [SERVICE] Cancelled attendance:`, response.data.data);
        return response.data.data;
      } else {
        throw new Error(
          response.data?.message || "Failed to cancel attendance"
        );
      }
    } catch (error) {
      console.error(
        "❌ [SERVICE] API failed for cancel attendance:",
        error.message
      );

      // Re-throw error instead of providing mock response
      throw error;
    }
  }

  /**
   * Get filter dropdown data (locations, types, prices)
   * Laravel equivalent: SpacesController@getDataForAvailableSpacesSearch
   * @returns {Promise<Object>} - Filter data (locations, types, bedrooms, prices)
   */
  async getFilterData() {
    try {
      console.log("🔍 [SERVICE] Getting filter data...");

      const response = await api.get("/spaces/filter-data");

      if (response.data?.success) {
        console.log("✅ [SERVICE] Filter data loaded:", response.data);
        return response.data;
      } else {
        throw new Error(response.data?.message || "Failed to get filter data");
      }
    } catch (error) {
      console.error("❌ [SERVICE] Error fetching filter data:", error);
      throw error;
    }
  }
}

const spacesService = new SpacesService();
export default spacesService;
